import { CircularProgress } from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import starIcon from '../../../common/images/staking/icon-star.svg';
import arrowUpIcon from '../../../common/svg/general/icon-arrow-up.svg';
import { useWeb3State } from '../../../hooks';
import { useStarsContract } from '../../../hooks/stars-contract.hooks';
import { WEB3_CONFIG } from '../../../lib';
import { Button } from '../../core/button';
import { Tooltip } from '../../core/tooltip';
import styles from './VotingForm.module.css';
export interface VotingFormProps {
  startBlock: {
    timestamp: number;
    number: number;
  };
  endBlock: {
    timestamp: number;
    number: number;
  };
  starsSubmitted: string;
  rewardGenerated: string;
  rewardsPerBlock: number;
  placeVote: (starsAmount: string) => Promise<boolean>;
  withdrawStars: (rewardsPerBlock: number) => void;
}

export interface VoteProps extends VotingFormProps {
  hideModal?: () => void;
}

export const VotingForm: React.FC<VoteProps> = props => {
  const { hideModal = () => {} } = props;
  const { walletDetails } = useWeb3State();
  const starsContract = useStarsContract();

  const [starsAmount, setStarsAmount] = useState<string>('');
  const [votesAmount, setVotesAmount] = useState<string>('');
  const [overBalanceError, setOverBalanceError] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [allowance, setAllowance] = useState(0);

  const getAllowance = async () => {
    const alnc = await starsContract.allowance(
      walletDetails.address ?? '',
      WEB3_CONFIG.contracts.bsc.movieVotingChef,
    );

    setAllowance(parseFloat(alnc));
  };

  useEffect(() => {
    void getAllowance();
  }, [starsContract.initialized]);

  const updateStars = (e: { target: { value: string } }) => {
    const newAmount = parseFloat(e.target.value);

    setOverBalanceError(
      newAmount > parseFloat(walletDetails.balance.stars.formatted)
        ? 'insufficient STARS balance'
        : '',
    );

    setStarsAmount(e.target.value);
    setVotesAmount(
      newAmount
        ? (
            Math.floor(
              Math.sqrt(newAmount + parseFloat(props.starsSubmitted)),
            ) - Math.floor(Math.sqrt(parseFloat(props.starsSubmitted)))
          ).toString()
        : '',
    );
  };

  const updateVotes = (e: { target: { value: string } }) => {
    const newAmount = parseInt(e.target.value.replaceAll('.', ''), 10);

    setOverBalanceError(
      newAmount * newAmount > parseFloat(walletDetails.balance.stars.formatted)
        ? 'insufficient STARS balance'
        : '',
    );

    setVotesAmount(e.target.value ? newAmount.toString() : '');
    setStarsAmount(
      e.target.value
        ? (
            Math.pow(
              newAmount +
                Math.floor(Math.sqrt(parseFloat(props.starsSubmitted))),
              2,
            ) - parseFloat(props.starsSubmitted)
          ).toString()
        : '',
    );
  };

  const handleClickWithdrawStars = () => {
    props.withdrawStars(props.rewardsPerBlock);
  };

  const handlePlaceVote = async () => {
    setSubmitting(true);
    const result = await props.placeVote(starsAmount);

    if (allowance === 0) {
      await getAllowance();
    } else if (result) {
      hideModal();
      toast.success('Vote Placed!');
    } else {
      toast.error('There was an error, try again later');
    }

    setSubmitting(false);
  };

  const votingDisabled =
    props.endBlock.timestamp > 0 || props.startBlock.timestamp === 0;

  return (
    <form className="w-full flex flex-col">
      <div
        style={{ zIndex: 1 }}
        className={
          'relative flex flex-col flex-grow mt-4' +
          (votingDisabled ? ' opacity-50' : '')
        }
      >
        <input
          type="number"
          placeholder="0"
          value={starsAmount}
          onChange={updateStars}
          disabled={votingDisabled}
          className={
            styles.votingInput +
            ' h-14 mx-6 border-0 rounded-lg outline-none text-right px-4'
          }
        />
        <div className={'absolute top-3 left-10 flex flex-row items-center'}>
          <div className="grid place-items-center w-8 h-8 bg-white bg-opacity-5 rounded-full">
            <img src={starIcon} alt="stars icon" />
          </div>
          <div className="ml-2 font-noto-sans font-bold text-lg">STARS</div>
        </div>

        <div
          style={{ zIndex: 1 }}
          className={
            styles.arrowDown +
            ' absolute top-11 left-1/2 w-10 h-10 flex flex-col items-center justify-center rounded-full transform -translate-x-1/2'
          }
        >
          <ArrowDownwardIcon />
        </div>
      </div>

      <div
        className={
          'relative flex flex-col flex-grow mt-4' +
          (votingDisabled ? ' opacity-50' : '')
        }
      >
        <input
          type="number"
          placeholder="0"
          value={votesAmount}
          onChange={updateVotes}
          disabled={votingDisabled}
          className={
            styles.votingInput +
            ' h-14 mx-6 border-0 rounded-lg outline-none text-right px-4' +
            (votingDisabled ? ' opacity-50' : '')
          }
        />

        <div className="absolute top-3 left-10 flex flex-row items-center">
          <div className="grid place-items-center w-8 h-8 bg-white bg-opacity-5 rounded-full">
            <img src={arrowUpIcon} alt="arrow up icon" />
          </div>
          <div className="ml-2 font-noto-sans font-bold text-lg">VOTES</div>
        </div>
      </div>

      <div className="h-6 ml-6 w-full text-red-500">{overBalanceError}</div>

      <div className="border-t border-gray opacity-10 mx-6"></div>

      <div className="flex flex-row justify-between mx-6 mt-4">
        <div className="text-secondary">STARS submitted</div>
        <div>
          {parseFloat(props.starsSubmitted).toLocaleString('en-us', {
            maximumFractionDigits: 3,
            useGrouping: true,
          })}{' '}
          STARS
        </div>
      </div>

      <div className="flex flex-row justify-between mx-6 mt-4">
        <div className="text-secondary">
          Your votes{' '}
          <Tooltip
            title="Mogul uses a quadratic voting formula. STARS get you the square root number of votes. For example, 10 STARS will get you 3 votes (rounded down)"
            placement="right"
          >
            <HelpOutlineIcon fontSize="small" className="mr-2 opacity-50" />
          </Tooltip>
        </div>
        <div>
          {Math.floor(
            Math.sqrt(parseFloat(props.starsSubmitted)),
          ).toLocaleString('en-us', {
            maximumFractionDigits: 0,
            useGrouping: true,
          })}{' '}
          VOTES
        </div>
      </div>

      {props.rewardsPerBlock > Math.pow(1, -19) && (
        <>
          <div className="flex flex-row justify-between mx-6 mt-4">
            <div className="text-secondary">Your rewards</div>
            <div>
              {parseFloat(props.rewardGenerated).toLocaleString('en-us', {
                maximumFractionDigits: 3,
                useGrouping: true,
              })}{' '}
              STARS
            </div>
          </div>

          <div className="flex flex-row justify-between mx-6 mt-4">
            <div className="text-secondary">Rewards per block</div>
            <div>
              {props.rewardsPerBlock.toLocaleString('en-us', {
                maximumFractionDigits: 2,
                useGrouping: true,
              })}{' '}
              STARS
            </div>
          </div>
        </>
      )}

      {!votingDisabled && (
        <div className="flex flex-row mx-6 mt-4">
          <Button
            className="w-1/3"
            kind="secondary"
            disabled={
              (props.rewardsPerBlock > Math.pow(1, -19) &&
                parseFloat(props.rewardGenerated) === 0) ||
              (props.rewardsPerBlock < Math.pow(1, -19) &&
                parseFloat(props.starsSubmitted) === 0)
            }
            onClick={handleClickWithdrawStars}
          >
            Withdraw
          </Button>
          <Button
            className="w-2/3 ml-4"
            disabled={!starsAmount || submitting || !!overBalanceError}
            onClick={handlePlaceVote}
          >
            {submitting ? (
              <CircularProgress style={{ color: 'white' }} />
            ) : allowance > 0 ? (
              'Vote'
            ) : (
              'Approve'
            )}
          </Button>
        </div>
      )}
    </form>
  );
};
